/**
 * <user-name .userId=${id}>
 *
 * The one way the application names a person (D-151, contact rules 6 and 8). It looks the person
 * up in the user directory by id and shows their display name, styled as a link, and nothing else
 * inline: never the username, no pill, no photo. Hovering over the name, reaching it with the
 * keyboard, or tapping it opens a card with their avatar, display name, username, email and phone;
 * the email writes to them (mailto:) and the phone calls them (tel:, phoneLinkTarget). A detail
 * the person does not have is left out of the card.
 *
 * Why the name is a button and not an anchor: it opens a card, it does not go anywhere, so it is a
 * button to assistive technology (with aria-expanded) and only LOOKS like a link.
 *
 * The card opens over the name through <agwa-hover-card>, which owns when it opens (hover, keyboard
 * focus, a tap), how it closes, and drawing it in the browser's top layer so no row, table or
 * dialog around the name can paint over it (U40 F1). This component only says what the name and
 * the card show.
 *
 * A host that sits the name on a coloured fill (a warning badge, a lock chip) sets `on-fill`, and
 * the name then takes the fill's text colour instead of the link colour, which would fight it.
 * An attribute rather than a ::part rule because most such hosts are table cells rendered inside
 * agwa-lit-table's shadow root, where the host component's stylesheet cannot reach.
 */
import { resolveAvatarUrl } from '../../utility/azure-blob-fetch';
import { phoneLinkTarget } from '../../utility/phone-number';
import { personName } from '../../utility/person-name';
import { terms } from '../../language/terms';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { userDirectoryInstance } from '../../global-data/user-directory-instance';
import { reloadWhenServerAnswers } from '../../api/unanswered-request';
import type { UserDirectoryUserDto } from '../../api/agwa-api-model';
import '@shoelace-style/shoelace/dist/components/avatar/avatar.js';
import '../hover-card/agwa-hover-card';
import type { HoverCardToggleDetail } from '../hover-card/agwa-hover-card';
import userNameStyles from './user-name-styles.css?inline';

@customElement('user-name')
export class UserNameElement extends LitElement {
  /** The person to name, by login user id. Empty renders nothing. */
  @property({ type: String }) userId: string = '';

  /** Set when the name sits on a coloured fill: the name takes the fill's text colour. */
  @property({ type: Boolean, reflect: true, attribute: 'on-fill' }) onFill =
    false;

  /** The directory's answer for userId; undefined while it is being looked up. */
  @state() private user?: UserDirectoryUserDto;
  /** True once the lookup has answered with no such person, so the name reads "Unknown". */
  @state() private notFound = false;
  /** Whether the card is showing. */
  @state() private open = false;

  private _unsubscribe?: () => void;
  private _subscribedUserId?: string;

  static styles = [unsafeCSS(userNameStyles)];

  connectedCallback(): void {
    super.connectedCallback();
    void this.tryResolveSyncThenAsync();
    this._subscribeToUserUpdates();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._unsubscribe?.();
    this._unsubscribe = undefined;
    this._subscribedUserId = undefined;
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('userId')) {
      void this.tryResolveSyncThenAsync();
      this._subscribeToUserUpdates();
    }
  }

  /**
   * Follow the directory's updates for this person, so an edit to them (a new name, a new phone)
   * reaches every place they are named without a reload.
   */
  private _subscribeToUserUpdates(): void {
    const id = this.userId?.trim();
    if (id === this._subscribedUserId) return;
    this._unsubscribe?.();
    this._unsubscribe = undefined;
    this._subscribedUserId = undefined;

    if (!id) return;

    const cache = userDirectoryInstance();
    this._subscribedUserId = id;
    this._unsubscribe = cache.subscribe(id, (updatedUser) => {
      this.user = updatedUser;
      this.notFound = false;
    });
  }

  /**
   * Answer from the directory cache at once when it already holds the person, otherwise ask the
   * server. A lookup the server answered with nobody marks the name unknown; one that got no
   * answer leaves it blank (D-144), since the person may well exist.
   */
  private async tryResolveSyncThenAsync() {
    const id = this.userId?.trim();
    this.notFound = false;
    if (!id) {
      this.user = undefined;
      return;
    }
    const cache = userDirectoryInstance();
    this.user = cache.getUserDtoSync(id);
    if (this.user) return;
    let answered: UserDirectoryUserDto | undefined;
    try {
      answered = await cache.getUserDto(id);
    } catch {
      // A lookup that threw got no answer: the name stays blank rather than claiming anything
      // (D-144), and the error is not left as an unhandled rejection from a render.
      return;
    }
    // A later userId may have replaced this one while the lookup was in flight.
    if (this.userId?.trim() !== id) return;
    this.user = answered;
    this.notFound = !answered && cache.isKnownAbsent(id);
    // A first load that got no answer waits for the server and asks again (D-144), so a name in
    // a banner or a row does not stay blank for the life of the page after a network blip.
    if (!answered && cache.wasUnanswered(id))
      reloadWhenServerAnswers({
        host: this,
        reload: () => void this.tryResolveSyncThenAsync(),
        isCurrent: () => this.userId?.trim() === id && !this.user,
      });
  }

  /** The hover card says when it opens and closes; the card's content is rendered only while open. */
  private onToggle = (e: CustomEvent<HoverCardToggleDetail>) => {
    this.open = e.detail.open;
  };

  /** The card: avatar, display name, username, then email and phone only where the person has them. */
  private renderCard(user: UserDirectoryUserDto, name: string) {
    const imageSrc = user.profileImageUrl
      ? resolveAvatarUrl(user.profileImageUrl)!
      : '/assets/images/profile-default.png';
    const email = user.emailAddress?.trim();
    const phone = user.phoneNumber?.trim();
    // Deleted and locked are facts about the account the card should not hide; the words are
    // plain adjectives, not domain terms.
    const status = user.isDeleted ? 'Deleted' : user.locked ? 'Locked' : null;
    return html`<div
      id="card"
      class="user-card"
      role="group"
      aria-label=${name}
    >
      <sl-avatar
        class="user-card-avatar"
        .image=${imageSrc}
        label=${name}
      ></sl-avatar>
      <div class="user-card-details">
        <div class="user-card-name">${name}</div>
        <div class="user-card-username">${user.userName}</div>
        ${
          status ? html`<div class="user-card-status">${status}</div>` : nothing
        }
        ${
          email
            ? html`<a class="user-card-email" href=${`mailto:${email}`}
                >${email}</a
              >`
            : nothing
        }
        ${
          phone
            ? html`<a class="user-card-phone" href=${phoneLinkTarget(phone)}
                >${phone}</a
              >`
            : nothing
        }
      </div>
    </div>`;
  }

  render() {
    if (!this.userId) return nothing;
    const user = this.user;
    if (!user) {
      // Still looking the person up, or nobody by that id: never a username, never the raw id.
      return this.notFound
        ? html`<span class="user-name-unknown"
            >Unknown ${terms.loginUser.lower}</span
          >`
        : html`<span class="user-name-pending" aria-busy="true"></span>`;
    }
    const name = personName(user.displayName);
    const stateClass = user.isDeleted
      ? 'state-deleted'
      : user.locked
        ? 'state-locked'
        : '';
    // Rendered only while open, so the name's text is the display name alone: anything reading
    // the text of a row or a banner (a journey, a screen reader's browse mode) finds the name,
    // never the hidden card's username, email and phone after it.
    const card = this.open ? this.renderCard(user, name) : nothing;
    // No whitespace between the tags: the name sits inline in sentences such as "by <name>.",
    // where any text node here would render as a stray space.
    return html`<span class="user-name-wrapper"
      ><agwa-hover-card @hover-card-toggle=${this.onToggle}
        ><button
          slot="anchor"
          type="button"
          class="user-name-link ${stateClass}"
          aria-expanded=${this.open ? 'true' : 'false'}
          aria-controls=${this.open ? 'card' : nothing}
        >
          ${name}</button
        >${card}</agwa-hover-card
      ></span
    >`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'user-name': UserNameElement;
  }
}
